import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { eventsApi } from '../lib/api';
import { useAuthStore } from '../store/authStore';

export type EventType =
  | 'OUTDOOR_IMAGE'
  | 'AD_IMAGE'
  | 'PROOF_OF_AD'
  | 'PROOF_OF_COMPANY'
  | 'MANUAL_APPROVAL_PROOF_OF_COMPANY'
  | 'APPROVED_PROOF_OF_COMPANY'
  | 'APPROVED_ACCESS_REQUEST'
  | 'REJECTED_ACCESS_REQUEST'
  | 'INVOICE'
  | 'CHAT_MESSAGE'
  // To whoever paid for a CONFIRMING campaign: a Placement Provider answered (or outdoors expired unanswered).
  | 'CAMPAIGN_FEEDBACK_RECEIVED'
  // Same audience: every provider answered (or the deadline passed) and the campaign moved on.
  | 'CAMPAIGN_CONFIRMATION_UPDATED';

export type PlatformEvent = {
  type: EventType;
  message: string;
  /** Set on campaign events. */
  campaignId?: string;
};

export type PlatformEventHandler = (event: PlatformEvent) => void;

const RECONNECT_DELAY_MS = 5_000;

export const usePlatformEvents = (onEvent?: PlatformEventHandler) => {
  const { isAuthenticated } = useAuthStore();
  const qc = useQueryClient();
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const destroyedRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    destroyedRef.current = false;
    hasConnectedRef.current = false;

    const createConnection = async () => {
      if (destroyedRef.current) return;
      try {
        const res = await eventsApi.subscribe();
        const url = res.data.url;
        if (!url || destroyedRef.current) return;

        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          // On reconnections only (not first connect): refresh queries that may
          // have gone stale while the socket was down (tab hidden, device slept).
          if (hasConnectedRef.current) {
            qc.invalidateQueries();
          }
          hasConnectedRef.current = true;
        };

        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data as string) as Record<string, unknown>;
            // Azure Web PubSub JSON protocol wraps app messages as
            // { type: 'message', from: 'server', data: <payload> }.
            // Fall back to the raw object when it doesn't match that envelope.
            const payload =
              msg['type'] === 'message' && msg['data'] != null
                ? (msg['data'] as Record<string, unknown>)
                : msg;
            if (payload['type'] && payload['message'] != null) {
              onEventRef.current?.(payload as unknown as PlatformEvent);
            }
          } catch {
            // non-JSON frames (e.g. ping/pong) are silently ignored
          }
        };

        ws.onclose = (ev) => {
          wsRef.current = null;
          // 1000 = clean close (logout, page unload). Anything else → reconnect.
          if (!destroyedRef.current && ev.code !== 1000) {
            // Re-call subscribe to get a fresh signed URL before reconnecting
            reconnectRef.current = setTimeout(createConnection, RECONNECT_DELAY_MS);
          }
        };

        ws.onerror = () => ws.close();
      } catch {
        // subscribe HTTP call failed (network error, 401, etc.) - retry later
        if (!destroyedRef.current) {
          reconnectRef.current = setTimeout(createConnection, RECONNECT_DELAY_MS);
        }
      }
    };

    // Re-subscribe if the socket silently dropped (tab hidden, device slept, etc.)
    const ensureConnected = () => {
      if (destroyedRef.current) return;
      const ws = wsRef.current;
      const isActive =
        ws !== null &&
        ws.readyState !== WebSocket.CLOSED &&
        ws.readyState !== WebSocket.CLOSING;
      if (!isActive) {
        if (reconnectRef.current) clearTimeout(reconnectRef.current);
        createConnection();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') ensureConnected();
    };

    const handleOnline = () => ensureConnected();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    createConnection();

    return () => {
      destroyedRef.current = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      wsRef.current?.close(1000);
      wsRef.current = null;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [isAuthenticated, qc]);
};
